/**
 * risk 域 Zod 共享契约 — 风控策略 ADM-USR-S007（方案 B-分步 · 超管限定版）
 *
 * 形状事实源：原型 design/Novel-admin RISK_RULES（7 条）+ 提案 §②/§③。
 *
 * 条件三列拆分（契约 §③，替代旧 condition DSL 文本）：
 * - condition_dimension 条件维度闭集（须 C 端判定支持）
 * - condition_operator  运算符闭集 eq / in / gt
 * - condition_value     比较值：eq/gt 标量、in 数组；与 dimension/operator 配套校验
 * 响应另派生只读 condition 展示串（dimension+operator+value 拼成，如 `client_ip.country == CN`），
 * 仅供 UI 渲染，非可写字段。
 *
 * action 机器码 auto_ban / observe（页面渲染中文「自动封禁 / 风控观察」由前端 adapter 映射）。
 * level high / medium / low。hits / last_hit_at 由 C 端风控引擎回写，admin 写接口不传（响应只读）。
 *
 * 本轮 MVP 仅开放超管两类写（拆端点）：
 * - PATCH /:id/enabled   → UpdateRiskRuleEnabledRequestSchema   { enabled }
 * - PATCH /:id/threshold → UpdateRiskRuleThresholdRequestSchema { condition_value 标量 }
 * 暂不开放新增 / 删除 / 改条件维度 / 改运算符 / 改动作 / 改名称（契约 §②/§⑤，依赖条件 DSL
 * schema + admin↔C 拉取契约就绪后列入后续）。旧 Create/UpdateRiskRuleRequestSchema 未用，保留不删。
 */

import { z } from 'zod';

/** 执行动作闭集：auto_ban = 自动封禁 / observe = 风控观察 */
export const RISK_RULE_ACTIONS = ['auto_ban', 'observe'] as const;
export type RiskRuleAction = (typeof RISK_RULE_ACTIONS)[number];

/** 风险等级闭集 */
export const RISK_RULE_LEVELS = ['high', 'medium', 'low'] as const;
export type RiskRuleLevel = (typeof RISK_RULE_LEVELS)[number];

/**
 * 条件维度闭集（契约 §③；须 C 端判定支持）。
 * - 阈值类（标量比较，配 gt）：rate_req_per_min / device_signup_24h / ip_signup_24h
 * - 枚举类（等值/集合，配 eq/in）：client_ip_country / phone_country_code / device_lang / email_domain
 */
export const RISK_RULE_DIMENSIONS = [
  'client_ip_country',
  'phone_country_code',
  'device_lang',
  'device_region',
  'rate_req_per_min',
  'device_signup_24h',
  'ip_signup_24h',
  'email_domain',
] as const;
export type RiskRuleDimension = (typeof RISK_RULE_DIMENSIONS)[number];

/** 运算符闭集：eq（==）/ in（IN 集合）/ gt（>）*/
export const RISK_RULE_OPERATORS = ['eq', 'in', 'gt'] as const;
export type RiskRuleOperator = (typeof RISK_RULE_OPERATORS)[number];

/**
 * (condition_dimension, condition_operator) 合法组合闭集（契约 §③）：
 * - rate_req_per_min / device_signup_24h / ip_signup_24h → 仅 gt（阈值类标量比较）
 * - client_ip_country / phone_country_code / email_domain → eq / in（枚举/集合）
 * - device_lang → eq / in
 */
export const RISK_RULE_DIMENSION_OPERATORS: Record<RiskRuleDimension, readonly RiskRuleOperator[]> =
  {
    rate_req_per_min: ['gt'],
    device_signup_24h: ['gt'],
    ip_signup_24h: ['gt'],
    client_ip_country: ['eq', 'in'],
    phone_country_code: ['eq', 'in'],
    email_domain: ['eq', 'in'],
    device_lang: ['eq', 'in'],
    device_region: ['eq', 'in'],
  };

/** 阈值类维度（condition_value 为数值标量、可经 threshold 端点改）= 配 gt 的维度 */
export const RISK_RULE_THRESHOLD_DIMENSIONS = [
  'rate_req_per_min',
  'device_signup_24h',
  'ip_signup_24h',
] as const;
export type RiskRuleThresholdDimension = (typeof RISK_RULE_THRESHOLD_DIMENSIONS)[number];

/** dimension/operator 是否为合法组合 */
export function isValidRiskRuleCombo(
  dimension: RiskRuleDimension,
  operator: RiskRuleOperator,
): boolean {
  return RISK_RULE_DIMENSION_OPERATORS[dimension].includes(operator);
}

/**
 * condition_value 类型校验（与 operator 配套）：
 * - eq / gt：标量（number 或 string，gt 语义上为 number 但存储不强约束以兼容）
 * - in：非空数组（元素 number 或 string）
 */
const RiskScalarSchema = z.union([z.number(), z.string()]);
export const RiskConditionValueSchema = z.union([
  RiskScalarSchema,
  z.array(RiskScalarSchema).min(1),
]);
export type RiskConditionValue = z.infer<typeof RiskConditionValueSchema>;

/**
 * 维度 + 运算符 + 值三者一致性校验（合法组合 + 值类型与 operator 配套）。
 * in 须数组、eq/gt 须标量；非法组合（如 rate_req_per_min 配 in）拒绝。
 * 返回错误信息数组（空数组 = 合法），供 service / route 归 VALIDATION_FAILED。
 */
export function validateRiskCondition(input: {
  condition_dimension: RiskRuleDimension;
  condition_operator: RiskRuleOperator;
  condition_value: RiskConditionValue;
}): string[] {
  const errs: string[] = [];
  const { condition_dimension, condition_operator, condition_value } = input;
  if (!isValidRiskRuleCombo(condition_dimension, condition_operator)) {
    errs.push(
      `operator '${condition_operator}' not allowed for dimension '${condition_dimension}'`,
    );
  }
  if (condition_operator === 'in') {
    if (!Array.isArray(condition_value) || condition_value.length === 0) {
      errs.push("operator 'in' requires a non-empty array value");
    }
  } else {
    // eq / gt：标量
    if (Array.isArray(condition_value)) {
      errs.push(`operator '${condition_operator}' requires a scalar value`);
    }
  }
  return errs;
}

/**
 * 创建风控规则入参（本轮 NOT-in-scope，未用 export 保留）。
 * hits / last_hit_at 由风控引擎维护，不接受客户端写入。
 */
export const CreateRiskRuleRequestSchema = z.object({
  name: z.string().min(1).max(128),
  condition_dimension: z.enum(RISK_RULE_DIMENSIONS),
  condition_operator: z.enum(RISK_RULE_OPERATORS),
  condition_value: RiskConditionValueSchema,
  action: z.enum(RISK_RULE_ACTIONS),
  level: z.enum(RISK_RULE_LEVELS),
  /** 默认启用，可显式传 false 先建后启 */
  enabled: z.boolean().default(true),
});
export type CreateRiskRuleRequest = z.infer<typeof CreateRiskRuleRequestSchema>;

/**
 * 更新风控规则入参（本轮 NOT-in-scope，未用 export 保留）。
 * 旧版「启停 + 改阈值」合并入参；本轮已拆为 enabled / threshold 两端点两 schema。
 */
export const UpdateRiskRuleRequestSchema = z
  .object({
    condition_value: RiskConditionValueSchema.optional(),
    enabled: z.boolean().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'At least one field must be provided',
  });
export type UpdateRiskRuleRequest = z.infer<typeof UpdateRiskRuleRequestSchema>;

/**
 * PATCH /:id/enabled 入参（超管启停 toggle）。
 */
export const UpdateRiskRuleEnabledRequestSchema = z.object({ enabled: z.boolean() }).strict();
export type UpdateRiskRuleEnabledRequest = z.infer<typeof UpdateRiskRuleEnabledRequestSchema>;

/**
 * PATCH /:id/threshold 入参（超管改已有规则阈值）。
 * 仅阈值类标量 condition_value（number 或 string；阈值类维度语义上为 number）；
 * 维度合法性（必须是阈值类维度）由 service 据规则现有 dimension 二次校验。
 */
export const UpdateRiskRuleThresholdRequestSchema = z
  .object({
    condition_value: z.union([z.number(), z.string()]),
  })
  .strict();
export type UpdateRiskRuleThresholdRequest = z.infer<typeof UpdateRiskRuleThresholdRequestSchema>;

export type RiskRuleResponse = {
  id: string;
  name: string;
  /** 条件三列（snake_case wire）*/
  condition_dimension: RiskRuleDimension;
  condition_operator: RiskRuleOperator;
  condition_value: RiskConditionValue;
  /** 派生只读展示串（dimension+operator+value 拼成，如 `client_ip.country == CN`）*/
  condition: string;
  action: RiskRuleAction;
  level: RiskRuleLevel;
  hits: number;
  last_hit_at: string | null;
  enabled: boolean;
  updated_by: string | null;
  /** 真实最后修改人的展示名；不以 UUID 或系统占位名替代。 */
  updated_by_name: string | null;
  created_at: string;
  updated_at: string;
};

/** 已发布 ADMIN v1 的只读响应别名；写入和存储仍使用 canonical RiskRuleAction。 */
export type RiskRuleV1Response = Omit<RiskRuleResponse, 'action'> & {
  action: 'ban' | 'observe';
};

/** 维度 → 展示串里的点分形式（与原型 `client_ip.country == CN` 对齐）*/
const DIMENSION_DISPLAY: Record<RiskRuleDimension, string> = {
  client_ip_country: 'client_ip.country',
  phone_country_code: 'phone.country_code',
  device_lang: 'device.lang',
  device_region: 'device.region',
  rate_req_per_min: 'rate.req_per_min',
  device_signup_24h: 'device.signup_24h',
  ip_signup_24h: 'ip.signup_24h',
  email_domain: 'email.domain',
};

/** 运算符 → 展示串符号 */
const OPERATOR_DISPLAY: Record<RiskRuleOperator, string> = {
  eq: '==',
  in: 'IN',
  gt: '>',
};

/**
 * 由三列拼派生只读 condition 展示串（前后端共用，保证两端一致）。
 * 标量：`client_ip.country == CN`；数组：`device.lang IN [zh-CN, zh-Hans]`。
 */
export function buildRiskConditionDisplay(input: {
  condition_dimension: RiskRuleDimension;
  condition_operator: RiskRuleOperator;
  condition_value: RiskConditionValue;
}): string {
  const dim = DIMENSION_DISPLAY[input.condition_dimension] ?? input.condition_dimension;
  const op = OPERATOR_DISPLAY[input.condition_operator] ?? input.condition_operator;
  const val = Array.isArray(input.condition_value)
    ? `[${input.condition_value.join(', ')}]`
    : String(input.condition_value);
  return `${dim} ${op} ${val}`;
}
